// src/pages/EstimateNew.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiRequest } from "../api";
import { ui, uiHandlers } from "../uiStyles";

export default function EstimateNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("job_id");

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [jobDetail, setJobDetail] = useState(null);

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");

      try {
        if (jobId) {
          const jobResult = await apiRequest(`/jobs/${jobId}/detail`);
          setJobDetail(jobResult);

          const jobClient = jobResult?.client;
          if (jobClient && jobClient.id) {
            setClients([jobClient]);
            setClientId(String(jobClient.id));
          } else {
            setClients([]);
            setClientId("");
            setErr("Project loaded, but no client was found.");
          }
        } else {
          const clientsResult = await apiRequest("/clients");
          const clientList = Array.isArray(clientsResult) ? clientsResult : [];
          setClients(clientList);

          if (clientList.length > 0) {
            setClientId(String(clientList[0].id));
          } else {
            setClientId("");
          }
        }
      } catch (e) {
        setErr(e.message || "Failed to load estimate form.");
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]);

  async function create() {
    if (!clientId) {
      setErr("Please select a client.");
      return;
    }

    setCreating(true);
    setErr("");

    try {
      const payload = {
        client_id: Number(clientId),
        job_id: jobId ? Number(jobId) : null,
        due_date: dueDate || null,
      };

      const est = await apiRequest("/estimates", {
        method: "POST",
        body: payload,
      });

      navigate(`/estimates/${est.id}`, { replace: true });
    } catch (e) {
      setErr(e.message || "Create failed.");
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return <div style={{ ...ui.page, color: "#ffffff" }}>Loading…</div>;
  }

  return (
    <div style={{ ...ui.page, maxWidth: 760 }}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/estimates" style={ui.link}>
          ← Back to Estimates
        </Link>
      </div>

      <div style={ui.card}>
        <h2 style={{ marginTop: 0, marginBottom: 8, color: "#ffffff" }}>
          Create Estimate
        </h2>

        <p style={{ marginTop: 0, marginBottom: 18, color: "#9fb0c3" }}>
          Start a new estimate by choosing a client and optional due date.
        </p>

        {err && (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 12px",
              borderRadius: 10,
              background: "rgba(127, 29, 29, 0.28)",
              border: "1px solid rgba(239, 68, 68, 0.35)",
              color: "#fecaca",
            }}
          >
            {err}
          </div>
        )}

        {jobDetail?.job ? (
          <div style={{ ...ui.subCard, marginBottom: 18 }}>
            <div
              style={{
                fontWeight: 700,
                marginBottom: 10,
                color: "#ffffff",
                fontSize: 16,
              }}
            >
              Creating estimate for this project
            </div>

            <div style={projectRowStyle}>
              <span style={projectLabelStyle}>Project</span>
              <span style={projectValueStyle}>
                {jobDetail.job.defendant_name || "—"}
              </span>
            </div>

            <div style={projectRowStyle}>
              <span style={projectLabelStyle}>Project #</span>
              <span style={projectValueStyle}>
                {jobDetail.job.case_number || "—"}
              </span>
            </div>

            <div style={projectRowStyle}>
              <span style={projectLabelStyle}>Client</span>
              <span style={projectValueStyle}>
                {jobDetail.client?.company_name ||
                  jobDetail.client?.contact_name ||
                  "—"}
              </span>
            </div>

            <div style={projectRowStyle}>
              <span style={projectLabelStyle}>Address</span>
              <span style={projectValueStyle}>
                {jobDetail.job.service_address || "—"}
              </span>
            </div>
          </div>
        ) : null}

        <div style={{ display: "grid", gap: 16, maxWidth: 480 }}>
          <label>
            <div style={labelStyle}>Client</div>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              style={ui.input}
              {...uiHandlers.focus}
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name || c.contact_name || `Client #${c.id}`}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div style={labelStyle}>Due Date</div>

<input
  type="date"
  value={dueDate}
  onChange={(e) => setDueDate(e.target.value)}
  style={{
    ...ui.input,
    colorScheme: "dark",
    cursor: "pointer",
  }}
  {...uiHandlers.focus}
/>
          </label>

          <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
            <button
              onClick={create}
              disabled={creating}
              style={ui.btnPrimary}
              {...uiHandlers.primaryHover}
            >
              {creating ? "Creating…" : "Create Draft"}
            </button>

            <Link
              to="/estimates"
              style={ui.btnSecondary}
              {...uiHandlers.secondaryHover}
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  marginBottom: 6,
  color: "#dbe7f3",
  fontWeight: 600,
  fontSize: 14,
};

const projectRowStyle = {
  display: "grid",
  gridTemplateColumns: "110px 1fr",
  gap: 12,
  padding: "6px 0",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
};

const projectLabelStyle = {
  color: "#9fb0c3",
  fontSize: 13,
};

const projectValueStyle = {
  color: "#ffffff",
  fontSize: 14,
};
